import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { BACKEND_URL } from '../lib/api';

const VerifyEmailPage = () => {
  const { token } = useParams();
  const [state, setState] = useState('verifying'); // verifying | verified | already | error
  const [message, setMessage] = useState('');
  const [role, setRole] = useState('customer');

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/auth/verify-email/${encodeURIComponent(token)}`);
        if (!active) return;
        const userRole = (res.data?.role || 'customer').toLowerCase();
        const isOrganiser = userRole === 'organiser';
        setRole(userRole);
        if (res.data?.status === 'already_verified') {
          setState('already');
          setMessage('Your email is already verified.');
        } else {
          setState('verified');
          setMessage(
            isOrganiser
              ? 'Email verified. You can now create events and manage your organiser account.'
              : 'Email verified. You can now access your VinePass account and manage your tickets.',
          );
        }
      } catch (e) {
        if (!active) return;
        setState('error');
        setMessage(e.response?.data?.detail || 'This verification link is invalid or has expired.');
      }
    })();
    return () => { active = false; };
  }, [token]);

  const Icon = state === 'verifying' ? Loader2 : (state === 'error' ? XCircle : CheckCircle2);
  const tone = state === 'error'
    ? { ring: 'ring-rose-100', icon: 'text-rose-500', heading: 'Verification Failed' }
    : state === 'verifying'
      ? { ring: 'ring-stone-100', icon: 'text-stone-500', heading: 'Verifying your email…' }
      : { ring: 'ring-emerald-100', icon: 'text-emerald-600', heading: state === 'already' ? 'Already Verified' : 'Email Verified' };

  // Customers land on /my-tickets; organisers on /dashboard. Errors → login.
  const successHref = role === 'organiser' ? '/dashboard' : '/my-tickets';
  const successLabel = role === 'organiser' ? 'Go to Dashboard' : 'View My Tickets';

  return (
    <div className="min-h-[80vh] bg-stone-50 py-16 px-4">
      <div className="max-w-md mx-auto bg-white border border-stone-200 rounded-2xl shadow-sm px-8 py-10 text-center">
        <div className={`w-20 h-20 rounded-full bg-white ring-8 ${tone.ring} flex items-center justify-center mx-auto mb-6`}>
          <Icon className={`w-10 h-10 ${tone.icon} ${state === 'verifying' ? 'animate-spin' : ''}`} strokeWidth={1.75} />
        </div>
        <h1 data-testid="verify-email-heading" className="font-serif text-3xl text-[#1F3D2B] mb-3">{tone.heading}</h1>
        <p data-testid="verify-email-message" className="text-stone-600 mb-8">{message || '\u00A0'}</p>
        {state !== 'verifying' && (
          <Link
            to={state === 'error' ? '/login' : successHref}
            data-testid="verify-email-cta"
            className="inline-block bg-[#1F3D2B] text-white px-6 py-3 rounded-full font-medium hover:bg-[#2a5239] transition-colors"
          >
            {state === 'error' ? 'Back to Login' : successLabel}
          </Link>
        )}
      </div>
    </div>
  );
};

export default VerifyEmailPage;
